package co.maytes.standupbot.dashboard.features.settings.sections

import co.maytes.standupbot.dashboard.components.motion.StateSwap
import co.maytes.standupbot.dashboard.features.settings.StatePhase
import co.maytes.standupbot.dashboard.features.settings.phase
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.widgets.Badge
import co.maytes.standupbot.dashboard.components.widgets.BadgeTone
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.SegmentedControl
import co.maytes.standupbot.dashboard.components.widgets.SegmentedOption
import co.maytes.standupbot.dashboard.features.integrations.LoadState
import co.maytes.standupbot.dashboard.features.logs.rememberLogsStore
import co.maytes.standupbot.dashboard.features.settings.LogCell
import co.maytes.standupbot.dashboard.features.settings.LogCellFlow
import co.maytes.standupbot.dashboard.features.settings.LogCellMissing
import co.maytes.standupbot.dashboard.features.settings.LogCellText
import co.maytes.standupbot.dashboard.features.settings.LogDetailList
import co.maytes.standupbot.dashboard.features.settings.LogLevelFilter
import co.maytes.standupbot.dashboard.features.settings.LogSeverity
import co.maytes.standupbot.dashboard.features.settings.LogSummary
import co.maytes.standupbot.dashboard.features.settings.LogTable
import co.maytes.standupbot.dashboard.features.settings.LogTableColumn
import co.maytes.standupbot.dashboard.features.settings.LogTableLoadingRows
import co.maytes.standupbot.dashboard.features.settings.LogTableRow
import co.maytes.standupbot.dashboard.features.settings.LogTimeFormat
import co.maytes.standupbot.dashboard.features.settings.LogsView
import co.maytes.standupbot.dashboard.features.settings.Plain
import co.maytes.standupbot.dashboard.features.settings.SettingsEmpty
import co.maytes.standupbot.dashboard.features.settings.SettingsError
import co.maytes.standupbot.dashboard.features.settings.SettingsGroup
import co.maytes.standupbot.dashboard.features.settings.SettingsPage
import co.maytes.standupbot.dashboard.features.settings.SettingsPanel
import co.maytes.standupbot.dashboard.features.settings.SettingsSubtitle
import co.maytes.standupbot.dashboard.features.settings.Strong
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.logs.LogEventDto
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

private const val LOADING_ROWS = 6

private val LogColumns = listOf(
    LogTableColumn("Level", width = "6rem"),
    LogTableColumn("Message", width = "100%"),
    LogTableColumn("Source", width = "12rem"),
    LogTableColumn("When (UTC)", end = true),
)

@Composable
fun LogsSection() {
    val auth = rememberAuthStore()
    val store = rememberLogsStore()
    val scope = rememberCoroutineScope()
    val workspaceId = auth.currentUser?.workspace?.id

    var filter by remember { mutableStateOf(LogLevelFilter.WARN) }
    var loadingMore by remember { mutableStateOf(false) }

    LaunchedEffect(workspaceId, filter) {
        if (workspaceId.isNullOrBlank()) return@LaunchedEffect
        store.load(workspaceId, filter.levels)
        while (true) {
            delay(LogsView.REFRESH_INTERVAL_MS)
            store.load(workspaceId, filter.levels)
        }
    }

    SettingsPage(
        group = SettingsGroup.SecurityAndData,
        title = "System logs",
        subtitle = if (store.loadState == LoadState.Ready) {
            { LogsSubtitle(LogsView.summary(store.events)) }
        } else {
            null
        },
    ) {
        Column(modifier = Modifier.fillMaxWidth().classNames(Motion.ENTER_CLASS)) {
            LogsToolbar(filter = filter, onFilter = { filter = it })
            StateSwap(state = if (store.events.isEmpty()) store.loadState.phase() else StatePhase.Ready) {
                when (store.loadState) {
                    LoadState.Idle, LoadState.Loading -> {
                        if (store.events.isEmpty()) {
                            LogTable(LogColumns, ariaLabel = "System logs") { LogTableLoadingRows(LogColumns, LOADING_ROWS) }
                        } else {
                            LogEventTable(store.events)
                        }
                    }
                    is LoadState.Error -> SettingsPanel {
                        SettingsError(what = LogsView.RESOURCE) {
                            val wid = workspaceId ?: return@SettingsError
                            scope.launch { store.load(wid, filter.levels) }
                        }
                    }
                    LoadState.Ready -> {
                        if (store.events.isEmpty()) {
                            SettingsEmpty(
                                text = LogsView.emptyText(filter),
                                action = if (filter != LogLevelFilter.ALL) {
                                    {
                                        Button(
                                            label = LogsView.SHOW_ALL,
                                            variant = ButtonVariant.Secondary,
                                            size = ButtonSize.Sm,
                                            onClick = { filter = LogLevelFilter.ALL },
                                        )
                                    }
                                } else {
                                    null
                                },
                            )
                        } else {
                            LogEventTable(store.events)
                            if (store.hasMore) {
                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(top = Spacing.md)
                                        .styleModifier { property("justify-content", "center") },
                                    verticalAlignment = Alignment.CenterVertically,
                                ) {
                                    Button(
                                        label = if (loadingMore) LogsView.LOADING_OLDER else LogsView.LOAD_OLDER,
                                        variant = ButtonVariant.Secondary,
                                        size = ButtonSize.Sm,
                                        enabled = !loadingMore,
                                        onClick = {
                                            val wid = workspaceId ?: return@Button
                                            loadingMore = true
                                            scope.launch {
                                                store.loadMore(wid, filter.levels)
                                                loadingMore = false
                                            }
                                        },
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun LogsSubtitle(summary: LogSummary) {
    SettingsSubtitle {
        Strong(summary.strong)
        Plain(summary.plain)
    }
}

@Composable
private fun LogsToolbar(filter: LogLevelFilter, onFilter: (LogLevelFilter) -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .margin(bottom = Spacing.md)
            .styleModifier {
                property("flex-wrap", "wrap")
                property("gap", Spacing.sm.toString())
            },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SegmentedControl(
            options = LogLevelFilter.entries.map { SegmentedOption(it, it.label) },
            selected = filter,
            onSelect = onFilter,
            ariaLabel = "Log level",
        )
        SpanText(
            text = LogsView.refreshNote(),
            modifier = Modifier.fontSize(Typography.bodySmall.size).color(Palette.textSecondary),
        )
    }
}

@Composable
private fun LogEventTable(events: List<LogEventDto>) {
    LogTable(LogColumns, ariaLabel = "System logs") {
        events.forEach { event -> key(event.id) { LogRow(event) } }
    }
}

@Composable
private fun LogRow(event: LogEventDto) {
    var expanded by remember { mutableStateOf(false) }
    val source = LogsView.sourceLabel(event.logger)

    LogTableRow(
        columnCount = LogColumns.size,
        expanded = expanded,
        onToggle = { expanded = !expanded },
        detail = { LogDetailList(LogsView.detailRows(event)) },
    ) {
        LogCell { LevelMark(event.level) }
        LogCell(flow = LogCellFlow.Wrap) { LogCellText(text = LogsView.preview(event.message)) }
        LogCell {
            if (source.isBlank()) {
                LogCellMissing()
            } else {
                LogCellText(
                    text = source,
                    mono = true,
                    clip = true,
                    capped = true,
                    color = Palette.textSecondary,
                    title = event.logger,
                )
            }
        }
        LogCell(end = true) {
            LogCellText(
                text = LogTimeFormat.seconds(event.createdAt),
                color = Palette.textSecondary,
                title = event.createdAt.toString(),
            )
        }
    }
}

@Composable
private fun LevelMark(level: String) {
    val label = LogsView.levelLabel(level)
    when (LogsView.severity(level)) {
        LogSeverity.Error -> Badge(label = label, tone = BadgeTone.Error)
        LogSeverity.Warn -> Badge(label = label, tone = BadgeTone.Warning)
        LogSeverity.Info -> LogCellText(text = label, color = Palette.textTertiary)
    }
}
